import { useQuery } from '@tanstack/react-query';

import type { LensState } from '~/pages/LeLens/document/types';

import { reportingLeLensPath } from '~/consts/reportingLeLensApi';

import { request } from '~/services/common';

import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import { reportingLeLensQueryKeys } from './useGetReportingLeLens';

export type BlockQuerySource =
  | { dataset: string; kind: 'catalogue'; label: string; catalogue: string; version?: string; table?: string }
  | { dataset: string; kind: 'sql'; label: string; sql?: string }
  | { dataset: string; kind: 'table'; label: string; rows: number; editedBy?: string; editedAt?: string };

export type BlockQueryFilter = {
  column: string;
  label: string;
  values: Array<string>;
  origin: 'query' | 'filter' | 'select';
  exclude?: true;
  range?: { min?: number; max?: number };
};

export type BlockQuery = {
  id: string;
  source: BlockQuerySource;
  measures: Array<{ id: string; label: string; certified: boolean; rollup: string; definition?: string; sql?: string }>;
  by: Array<string>;
  period: { label: string; current: string; comparisons: Array<string> };
  filters: Array<BlockQueryFilter>;
  sql?: string;
};

export type BlockLineage = {
  lensId: string;
  lensVersion: number;
  block: string;
  name?: string;
  sqlWithheld?: string;
  notApplied: Array<{ column: string; label: string }>;
  queries: Array<BlockQuery>;
};

export function useGetReportingLensBlockQuery(
  lensId: string,
  lensVersion: number | undefined,
  blockId: string | undefined,
  state: LensState,
) {
  return useQuery({
    queryKey: [...reportingLeLensQueryKeys.detail(lensId), 'block-query', lensVersion, blockId, state],
    enabled: Boolean(blockId && lensVersion),
    queryFn: async ({ signal }) =>
      (await request(
        resolveReportingLeLensUrl(`${reportingLeLensPath(lensId)}/blocks/${encodeURIComponent(blockId!)}/query`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ state, lensVersion }),
          signal,
        },
      )) as BlockLineage,
    retry: false,
    staleTime: 60_000,
  });
}
